/**
 * Сезон (ISSUE-74): 28 дней по календарю, очки за принятые сервером действия с дневным потолком, шкала с двумя
 * дорожками. Основная волна сезоном не сбрасывается. Монеты шага считаются от лучшей волны на момент получения,
 * поэтому шаг, забранный позже, не обесценивается. Сервер хранит только очки и что забрано (`SeasonState`).
 */
import type { BalanceData, HeroProgress, SeasonDef, SeasonReward, SeasonState } from "@newfungame/contracts";
import { zombieCount } from "../sim/waves";
import { waveBaseReward } from "./income";
import { DAY_MS, streakDayIndex, streakDayStartMs } from "./streak";

export interface SeasonClock {
  number: number;
  /** День сезона, с 0. */
  day: number;
  /** Сколько дней можно ещё копить очки, включая сегодня. */
  daysLeft: number;
  endsAt: number;
}

export interface SeasonStepView {
  step: number;
  free: SeasonReward;
  pass: SeasonReward;
  freeClaimed: boolean;
  passClaimed: boolean;
  reached: boolean;
}

const NOTHING: SeasonReward = { coins: 0, crystals: 0, skip: 0, speed: 0 };

/** Какой сезон идёт в момент `nowMs`; `null` — сезона нет в данных или первый ещё не начался. */
export function seasonAt(b: BalanceData, nowMs: number): SeasonClock | null {
  const def = b.season;
  if (!def) return null;
  const since = streakDayIndex(b, nowMs) - def.epochDay;
  if (since < 0) return null;
  const n = Math.floor(since / def.lengthDays);
  const day = since - n * def.lengthDays;
  const endDay = def.epochDay + (n + 1) * def.lengthDays;
  return { number: n + 1, day, daysLeft: def.lengthDays - day, endsAt: streakDayStartMs(b, endDay) };
}

/** Сколько дней сезона осталось, включая сегодня, по `SeasonState.endsAt`. */
export function seasonDaysLeft(endsAt: number, nowMs: number): number {
  return Math.max(1, Math.ceil((endsAt - nowMs) / DAY_MS));
}

/** Очки за журнал волны: доля убитых от числа зомби волны и босс. */
export function seasonWavePoints(
  b: BalanceData,
  def: SeasonDef,
  wave: number,
  kills: number,
  bossKilled: boolean,
): number {
  const share = Math.min(1, kills / zombieCount(b, wave));
  return Math.floor(share * def.points.perClearedWave) + (bossKilled ? def.points.boss : 0);
}

/** Сколько из `raw` зачислить при `todayPoints` за сегодня. */
export function seasonCredit(def: SeasonDef, todayPoints: number, raw: number): number {
  return Math.max(0, Math.min(raw, def.dailyCap - todayPoints));
}

export function seasonStep(def: SeasonDef, points: number): number {
  return Math.min(def.steps, Math.floor(points / def.pointsPerStep));
}

/** Последний шаг, до которого ещё можно дойти: вошедший в середине сезона видит только его. */
export function seasonLastStep(
  def: SeasonDef,
  s: Pick<SeasonState, "points" | "todayPoints">,
  daysLeft: number,
): number {
  const today = Math.max(0, def.dailyCap - s.todayPoints);
  return seasonStep(def, s.points + today + (Math.max(1, daysLeft) - 1) * def.dailyCap);
}

const every = (step: number, n: number) => step % n === 0;

/** Награда шага на дорожке при лучшей волне `bestWave`. */
export function seasonReward(
  b: BalanceData,
  def: SeasonDef,
  step: number,
  track: "free" | "pass",
  bestWave: number,
): SeasonReward {
  const base = waveBaseReward(b, Math.max(1, bestWave));
  if (track === "free") {
    const f = def.free;
    return { ...NOTHING, coins: f.coinWaves * base, crystals: every(step, f.crystalsEvery) ? f.crystals : 0 };
  }
  const p = def.pass;
  return {
    coins: p.coinWaves * base,
    crystals: every(step, p.crystalsEvery) ? p.crystals : 0,
    skip: every(step, p.skipEvery) ? 1 : 0,
    speed: every(step, p.speedEvery) ? 1 : 0,
  };
}

const add = (a: SeasonReward, b: SeasonReward): SeasonReward => ({
  coins: a.coins + b.coins,
  crystals: a.crystals + b.crystals,
  skip: a.skip + b.skip,
  speed: a.speed + b.speed,
});

/** Всё, что открыто и не забрано; дорожка пропуска — только с пропуском, зато сразу за все пройденные шаги. */
export function seasonUnclaimed(b: BalanceData, def: SeasonDef, s: SeasonState, bestWave: number): SeasonReward {
  const reached = seasonStep(def, s.points);
  let sum = NOTHING;
  for (let i = s.claimedFree + 1; i <= reached; i++) sum = add(sum, seasonReward(b, def, i, "free", bestWave));
  if (s.pass) {
    for (let i = s.claimedPass + 1; i <= reached; i++) sum = add(sum, seasonReward(b, def, i, "pass", bestWave));
  }
  return sum;
}

/** Шаги для окна сезона: только те, до которых ещё можно дойти. */
export function seasonSteps(
  b: BalanceData,
  def: SeasonDef,
  s: SeasonState,
  bestWave: number,
  daysLeft: number,
): SeasonStepView[] {
  const reached = seasonStep(def, s.points);
  const last = Math.max(reached, seasonLastStep(def, s, daysLeft));
  return Array.from({ length: last }, (_, i) => {
    const step = i + 1;
    return {
      step,
      free: seasonReward(b, def, step, "free", bestWave),
      pass: seasonReward(b, def, step, "pass", bestWave),
      freeClaimed: step <= s.claimedFree,
      passClaimed: step <= s.claimedPass,
      reached: step <= reached,
    };
  });
}

/** Ближайший закрытый герой, если до его волны не дальше `heroWavesAhead`; иначе `null`. */
export function earlyHero(
  b: BalanceData,
  def: SeasonDef,
  heroes: readonly HeroProgress[],
  bestWave: number,
): HeroProgress["heroId"] | null {
  const locked = new Set(heroes.filter((h) => !h.unlocked).map((h) => h.heroId));
  const next = b.heroes.filter((h) => locked.has(h.id)).sort((x, y) => x.unlockWave - y.unlockWave)[0];
  return next && next.unlockWave - bestWave <= def.shop.heroWavesAhead ? next.id : null;
}
